import * as React from "react";

import { Fragment, useState } from "react";

// react-router components
import { Link } from "react-router-dom";

// @mui material components
import Container from "@mui/material/Container";
import Icon from "@mui/material/Icon";
import Popper from "@mui/material/Popper";
import Grow from "@mui/material/Grow";
import Grid from "@mui/material/Grid";
import Divider from "@mui/material/Divider";
import MuiLink from "@mui/material/Link";

// React components
import MKTypography from "../../../components/MKTypography";
import MKBox from "../../../components/MKBox";

// React example components
import DefaultNavbarDropdown from "./DefaultNavbarDropdown";
import breakpoints from "../../../assets/theme/base/breakpoints";
import MKButton from "../../../components/MKButton";
import DefaultNavbarMobile from "./DefaultNavbarMobile";

import jsonData from '../../../apiDetailsConfig.json';

function DefaultNavbar({
  brand,
  routes,
  transparent,
  light,
  action,
  sticky,
  relative,
  center
}: any) {
  const [dropdown, setDropdown]: any = useState("");
  const [dropdownEl, setDropdownEl]: any = useState("");
  const [dropdownName, setDropdownName]: any = useState("");
  const [nestedDropdown, setNestedDropdown]: any = useState("");
  const [nestedDropdownEl, setNestedDropdownEl]: any = useState("");
  const [nestedDropdownName, setNestedDropdownName]: any = useState("");
  const [arrowRef, setArrowRef]: any = useState(null);
  const [mobileNavbar, setMobileNavbar] = useState(false);
  const [mobileView, setMobileView] = useState(false);

  const openMobileNavbar = () => { setMobileNavbar(!mobileNavbar); };

  React.useEffect(() => {
    // A function that sets the display state for the DefaultNavbarMobile.
    function displayMobileNavbar() {
      if (window.innerWidth < breakpoints.values.lg) {
        setMobileView(true);
        setMobileNavbar(false);
      } else {
        setMobileView(false);
        setMobileNavbar(false);
      }
    }

    /**
     The event listener that's calling the displayMobileNavbar function when
     resizing the window.
    */
    window.addEventListener("resize", displayMobileNavbar);

    // Call the displayMobileNavbar function to set the state with the initial value.
    displayMobileNavbar();

    // Remove event listener on cleanup
    return () => { window.removeEventListener("resize", displayMobileNavbar); };
  }, []);

  const renderNavbarItems = routes.map(
    ({ name, icon, href, route, collapse }: any) => (
      <DefaultNavbarDropdown
        key={ name }
        name={ name }
        icon={ icon }
        href={ href }
        route={ route }
        collapse={ Boolean(collapse) }
        onMouseEnter={ ({ currentTarget }: any) => {
          if (collapse) {
            setDropdown(currentTarget);
            setDropdownEl(currentTarget);
            setDropdownName(name);
          }
        } }
        onMouseLeave={ () => collapse && setDropdown(null) }
        light={ light }
      />
    )
  );

  // Render the routes on the dropdown menu
  const renderRoutes = routes.map(
    ({ name, collapse, columns, rowsPerColumn }: any) => {
      let template;

      // Render the dropdown menu that should be display as columns
      if (collapse && columns && name === dropdownName) {
        const calculateColumns = collapse.reduce(
          (resultArray: any, item: any, index: any) => {
            const chunkIndex = Math.floor(index / rowsPerColumn);

            if (!resultArray[chunkIndex]) {
              resultArray[chunkIndex] = [];
            }

            resultArray[chunkIndex].push(item);

            return resultArray;
          },
          []
        );

        template = (
          <Grid key={ name } container spacing={ 3 } py={ 1 } px={ 1.5 }>
            { calculateColumns.map((cols: any, key: any) => {
              const gridKey = `grid-${key}`;
              const dividerKey = `divider-${key}`;

              return (
                <Grid
                  key={ gridKey }
                  item
                  xs={ 12 / columns }
                  sx={ { position: "relative" } }
                >
                  { cols.map((col: any, index: any) => (
                    <Fragment key={ col.name }>
                      <MKTypography
                        display="block"
                        variant="button"
                        fontWeight="bold"
                        textTransform="capitalize"
                        py={ 1 }
                        px={ 0.5 }
                        mt={ index !== 0 ? 2 : 0 }
                      >
                        { col.name }
                      </MKTypography>
                      { col.collapse.map((item: any) => (
                        <MKTypography
                          key={ item.name }
                          component={ item.route ? Link : MuiLink }
                          to={ item.route ? item.route : "" }
                          href={
                            item.href
                              ? item.href
                              : (e: any) => e.preventDefault()
                          }
                          target={ item.href ? "_blank" : "" }
                          rel={ item.href ? "noreferrer" : "noreferrer" }
                          minWidth="11.25rem"
                          display="block"
                          variant="button"
                          color="text"
                          textTransform="capitalize"
                          fontWeight="regular"
                          py={ 0.625 }
                          px={ 2 }
                          sx={ ({
                            palette: { grey, dark },
                            borders: { borderRadius }
                          }: any) => ({
                            borderRadius: borderRadius.md,
                            cursor: "pointer",
                            transition: "all 300ms linear",

                            "&:hover": {
                              backgroundColor: grey[200],
                              color: dark.main
                            }
                          }) }
                        >
                          { item.name }
                        </MKTypography>
                      )) }
                    </Fragment>
                  )) }
                  { key !== 0 && (
                    <Divider
                      key={ dividerKey }
                      orientation="vertical"
                      sx={ {
                        position: "absolute",
                        top: "50%",
                        left: "-4px",
                        transform: "translateY(-45%)",
                        height: "90%"
                      } }
                    />
                  ) }
                </Grid>
              );
            }) }
          </Grid>
        );

        // Render the dropdown menu that should be display as list items
      } else if (collapse && name === dropdownName) {
        template = collapse.map((item: any) => {
          const linkComponent = {
            component: MuiLink,
            href: item.href,
            target: "_blank",
            rel: "noreferrer"
          };

          const routeComponent = {
            component: Link,
            to: item.route
          };

          return (
            <MKTypography
              key={ item.name }
              { ...(item.route ? routeComponent : linkComponent) }
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              variant="button"
              textTransform="capitalize"
              minWidth={ item.description ? "14rem" : "12rem" }
              color={ item.description ? "dark" : "text" }
              fontWeight={ item.description ? "bold" : "regular" }
              py={ item.description ? 1 : 0.625 }
              px={ 2 }
              sx={ ({
                palette: { grey, dark },
                borders: { borderRadius }
              }: any) => ({
                borderRadius: borderRadius.md,
                cursor: "pointer",
                transition: "all 300ms linear",

                "&:hover": {
                  backgroundColor: grey[200],
                  color: dark.main,

                  "& *": {
                    color: dark.main
                  }
                }
              }) }
              onMouseEnter={ ({ currentTarget }: any) => {
                if (item.dropdown) {
                  setNestedDropdown(currentTarget);
                  setNestedDropdownEl(currentTarget);
                  setNestedDropdownName(item.name);
                }
              } }
              onMouseLeave={ () => {
                if (item.dropdown) {
                  setNestedDropdown(null);
                }
              } }
            >
              { item.description ? (
                <MKBox>
                  { item.name }
                  <MKTypography
                    display="block"
                    variant="button"
                    color="text"
                    fontWeight="regular"
                    sx={ { transition: "all 300ms linear" } }
                  >
                    { item.description }
                  </MKTypography>
                </MKBox>
              ) : (
                item.name
              ) }
              { item.collapse && (
                <Icon
                  fontSize="small"
                  sx={ {
                    fontWeight: "normal",
                    verticalAlign: "middle",
                    mr: -0.5
                  } }
                >
                  keyboard_arrow_right
                </Icon>
              ) }
            </MKTypography>
          );
        });
      }

      return template;
    }
  );

  // Routes dropdown menu
  const dropdownMenu = (
    <Popper
      anchorEl={ dropdown }
      popperRef={ null }
      open={ Boolean(dropdown) }
      placement="top-start"
      transition
       
      style={ { zIndex: 10 } }
      modifiers={ [
        {
          name: "arrow",
          enabled: true,
          options: {
            element: arrowRef
          }
        }
      ] }
      onMouseEnter={ () => setDropdown(dropdownEl) }
      onMouseLeave={ () => {
        if (!nestedDropdown) {
          setDropdown(null);
          setDropdownName("");
        }
      } }
    >
      { ({ TransitionProps }) => (
        <Grow { ...TransitionProps }>
          <MKBox borderRadius="lg" bgcolor="white">
            <MKTypography variant="h1" color="white">
              <Icon ref={ setArrowRef } sx={ { mt: -3 } }>
                arrow_drop_up
              </Icon>
            </MKTypography>
            <MKBox
              shadow="lg"
              borderRadius="lg"
              p={ 2 }
              mt={ 2 }
              sx={ { backgroundColor: "#fff" } }
            >
              { renderRoutes }
            </MKBox>
          </MKBox>
        </Grow>
      ) }
    </Popper>
  );

  // Render routes that are nested inside the dropdown menu routes
  const renderNestedRoutes = routes.map(({ collapse, columns }: any) =>
    collapse && !columns
      ? collapse.map(({ name: parentName, collapse: nestedCollapse }: any) => {
        let template;

        if (parentName === nestedDropdownName) {
          template =
              nestedCollapse?.map((item: any) => {
                const linkComponent = {
                  component: MuiLink,
                  href: item.href,
                  target: "_blank",
                  rel: "noreferrer"
                };

                const routeComponent = {
                  component: Link,
                  to: item.route
                };

                return (
                  <MKTypography
                    key={ item.name }
                    { ...(item.route ? routeComponent : linkComponent) }
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    variant="button"
                    textTransform="capitalize"
                    minWidth={ item.description ? "14rem" : "12rem" }
                    color={ item.description ? "dark" : "text" }
                    fontWeight={ item.description ? "bold" : "regular" }
                    py={ item.description ? 1 : 0.625 }
                    px={ 2 }
                    sx={ ({
                      palette: { grey, dark },
                      borders: { borderRadius }
                    }: any) => ({
                      borderRadius: borderRadius.md,
                      cursor: "pointer",
                      transition: "all 300ms linear",

                      "&:hover": {
                        backgroundColor: grey[200],
                        color: dark.main,

                        "& *": {
                          color: dark.main
                        }
                      }
                    }) }
                  >
                    { item.description ? (
                      <MKBox>
                        { item.name }
                        <MKTypography
                          display="block"
                          variant="button"
                          color="text"
                          fontWeight="regular"
                          sx={ { transition: "all 300ms linear" } }
                        >
                          { item.description }
                        </MKTypography>
                      </MKBox>
                    ) : (
                      item.name
                    ) }
                    { item.collapse && (
                      <Icon
                        fontSize="small"
                        sx={ {
                          fontWeight: "normal",
                          verticalAlign: "middle",
                          mr: -0.5
                        } }
                      >
                        keyboard_arrow_right
                      </Icon>
                    ) }
                  </MKTypography>
                );
              });
        }

        return template;
      })
      : null
  );

  // Dropdown menu for the nested dropdowns
  const nestedDropdownMenu = (
    <Popper
      anchorEl={ nestedDropdown }
      popperRef={ null }
      open={ Boolean(nestedDropdown) }
      placement="right-start"
      transition
      style={ { zIndex: 10 } }
      onMouseEnter={ () => {
        setNestedDropdown(nestedDropdownEl);
      } }
      onMouseLeave={ () => {
        setNestedDropdown(null);
        setNestedDropdownName("");
        setDropdown(null);
      } }
    >
      { ({ TransitionProps }) => (
        <Grow { ...TransitionProps }>
          <MKBox ml={ 2.5 } mt={ -2.5 } borderRadius="lg">
            <MKBox shadow="lg" borderRadius="lg" py={ 1.5 } px={ 1 } mt={ 2 }>
              { renderNestedRoutes }
            </MKBox>
          </MKBox>
        </Grow>
      ) }
    </Popper>
  );

  return (
    <Container
      maxWidth={ false }
      style={ { padding: 0, minWidth: "100%" } }
      sx={ sticky ? { position: "sticky", top: 0, zIndex: 10 } : null }
    >
      <MKBox
        py={ 1 }
        px={ { xs: 4, sm: transparent ? 2 : 3, lg: transparent ? 0 : 2 } }
        width="100%"
        // borderRadius="xl"
        shadow={ transparent ? "none" : "md" }
        color={ light ? "white" : "dark" }
        position={ relative ? "relative" : "absolute" }
        zIndex={ 3 }
        sx={ ({
          palette: { transparent: transparentColor, white },
          functions: { rgba }
        }: any) => ({
          margin: 0,
          backgroundColor: transparent
            ? transparentColor.main
            : rgba(white.main, 0.8),
          backdropFilter: transparent ? "none" : `saturate(200%) blur(30px)`
        }) }
      >
        <MKBox
          display="flex"
          justifyContent="space-between"
          alignItems="center"
        >
          <MKBox
            component={ Link }
            to="/"
            lineHeight={ 2 } // test
            py={ transparent ? 1.5 : 0.75 }
            pl={ relative || transparent ? 0 : { xs: 6, lg: 1 } }
          >
            <MKTypography
              fontWeight="bold"
              color={ light ? "white" : "dark" }
              style={ { display: "flex", fontSize: "0.975rem" } }
            >
              { brand }
            </MKTypography>
            <MKTypography
              fontWeight="bold"
              color={ light ? "white" : "dark" }
              style={ { display: "flex", fontSize: "0.675rem", alignItems: "center" } }
            >
              <MKTypography style={ { fontSize: "12px" } }>
                Powered By &nbsp;
              </MKTypography>
              BeSLighthouse { jsonData.version }
            </MKTypography>
          </MKBox>
          <MKBox
            color="inherit"
            display={ { xs: "none", lg: "flex" } }
            ml="auto"
            mr={ center ? "auto" : 0 }
          >
            { renderNavbarItems }
          </MKBox>
          <MKBox ml={ { xs: "auto", lg: 0 } }>
            { action &&
              (action.type === "internal" ? (
                <MKButton
                  component={ Link }
                  to={ action.route }
                  variant={
                    action.color === "white" || action.color === "default"
                      ? "contained"
                      : "gradient"
                  }
                  color={ action.color ? action.color : "info" }
                  size="small"
                >
                  { action.label }
                </MKButton>
              ) : (
                <MKButton
                  component="a"
                  href={ action.route }
                  target="_blank"
                  rel="noreferrer"
                  variant={
                    action.color === "white" || action.color === "default"
                      ? "contained"
                      : "gradient"
                  }
                  color={ action.color ? action.color : "info" }
                  size="small"
                >
                  { action.label }
                </MKButton>
              )) }
          </MKBox>
          <MKBox
            display={ { xs: "inline-block", lg: "none" } }
            lineHeight={ 0 }
            py={ 1.5 }
            pl={ 0 }
            color={ transparent ? "white" : "inherit" }
            sx={ { cursor: "pointer", position: "absolute" } }
            onClick={ openMobileNavbar }
          >
            <Icon>{ mobileNavbar ? "close" : "menu" }</Icon>
          </MKBox>
        </MKBox>
        <MKBox
          bgColor={ transparent ? "white" : "transparent" }
          shadow={ transparent ? "lg" : "none" }
          borderRadius="xl"
          px={ transparent ? 2 : 0 }
        >
          { mobileView && (
            <DefaultNavbarMobile routes={ routes } open={ mobileNavbar } />
          ) }
        </MKBox>
      </MKBox>
      { dropdownMenu }
      { nestedDropdownMenu }
    </Container>
  );
}

// Setting default values for the props of DefaultNavbar
DefaultNavbar.defaultProps = {
  brand: jsonData.labName,
  transparent: false,
  light: false,
  action: false,
  sticky: false,
  relative: false,
  center: false
};

export default DefaultNavbar;
